import { faX } from "@fortawesome/free-solid-svg-icons/faX";
import { Modal as AntdModal } from "../../utils/local-antd";
import { ModalProps as AntdModalProps } from "../../utils/local-antd";
import { t } from "i18next";
import { useState } from "react";
import { Fragment } from "react/jsx-runtime";
import styled from "styled-components";

import { Spacing } from "../../theme/Spacing";
import { Button } from "../Button";
import { Flex } from "../Flex";
import { FontSize } from "../Text";
import {
  TextWithTruncated,
  TextWithTruncatedProps,
} from "../TextWithTruncated";
import { WarningModal } from "../WarningModal";

const StyledAntdModal = styled(AntdModal)`
  .ant-modal-close {
    display: none;
  }
`;

const ModalTitleContainer = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  background-color: ${({ theme }) => theme.white};
  gap: ${Spacing.s};
`;

const CloseButton = styled(Button)`
  margin: 0;
  padding: 0;
  background-color: ${({ theme }) => theme.transparent};
  color: ${({ theme }) => theme.gray};
`;

const StyledTitle = styled(TextWithTruncated)`
  font-size: ${FontSize.l} !important;
  font-weight: bold;
  text-align: start !important;
  color: ${({ theme }) => theme.gray};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledHr = styled.hr`
  height: 1.5px;
  opacity: 1;
  background-color: ${({ theme }) => theme.lightgray};
  width: 100%;
  border: 0;
  margin-bottom: ${Spacing.m};
`;

interface ModalProps extends AntdModalProps {
  children: React.ReactNode;
  title?: string;
  titleProps?: TextWithTruncatedProps;
  content?: React.CSSProperties;
  overlay?: React.CSSProperties;
  open: boolean;
  onClose?: VoidFunction;
  className?: string;
  showCloseWarning?: boolean;
  closeWarningDescription?: string;
  onConfirmClose?: VoidFunction;
  /**
   * Keep the custom close (X) button even when `maskClosable={false}` disables
   * click-outside dismissal. Pair with `showCloseWarning` for a "close only via
   * X, and confirm first" modal.
   */
  forceClosable?: boolean;
}

export const Modal: React.FC<ModalProps> = ({
  onClose,
  children,
  content,
  overlay,
  title,
  titleProps = {},
  open,
  className,
  closeWarningDescription,
  showCloseWarning = false,
  onConfirmClose,
  forceClosable = false,
  ...props
}) => {
  const [warningModalVisible, setWarningModalVisible] = useState(false);

  const onLocalClose = showCloseWarning
    ? () => setWarningModalVisible(true)
    : onClose;

  const onLocalConfirmClose = () => {
    setWarningModalVisible(false);
    onConfirmClose?.();
    onClose?.();
  };

  return (
    <Fragment>
      <StyledAntdModal
        className={className}
        open={open}
        onCancel={onLocalClose}
        footer={null}
        title={
          <ModalTitleContainer className="atira-modal-title-container">
            {title ? <StyledTitle {...titleProps}>{title}</StyledTitle> : null}
            {props.maskClosable !== false || forceClosable ? (
              <CloseButton icon={faX} iconWidth="lg" onClick={onLocalClose} />
            ) : null}
          </ModalTitleContainer>
        }
        {...props}
        zIndex={props?.zIndex || 2001}
      >
        <Flex
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
        >
          <StyledHr />

          {children}
        </Flex>
      </StyledAntdModal>

      {showCloseWarning ? (
        <WarningModal
          description={closeWarningDescription || t("warning.sure_to_leave")}
          isOpen={warningModalVisible}
          onClose={() => setWarningModalVisible(false)}
          onConfirm={onLocalConfirmClose}
        />
      ) : null}
    </Fragment>
  );
};
